import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { XCircle, Upload, X, AlertCircle } from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { DeleteConfirmationDialog } from "@shared/components/DeleteConfirmationDialog";
import {
  useRejectionReason,
  useRejectServiceOrder,
} from "@features/wx/dispatcher/hooks";
import { useParams } from "react-router-dom";
import { useUserName } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/hooks/use-toast";

interface RejectServiceOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobCardId: string;
  onSuccess?: () => void;
}

export interface RejectionData {
  reason: string;
  comments: string;
  attachments: File[];
}

const RejectServiceOrderModal: React.FC<RejectServiceOrderModalProps> = ({
  isOpen,
  onClose,
  jobCardId,
  onSuccess,
}) => {
  const { id } = useParams();
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const { data: rejectionReasonData } = useRejectionReason();
  const rejectServiceOrder = useRejectServiceOrder();
  const userName = useUserName();
  const rejectionReasonOptions =
    rejectionReasonData?.result?.map((item: any) => ({
      value: item.key,
      label: item.value,
    })) || [];

  // Define form fields
  const formFields: FormField[] = [
    {
      name: "reason",
      label: "Rejection Reason",
      type: "select",
      required: true,
      placeholder: "Select a rejection reason",
      options: rejectionReasonOptions,
      helperText: "Choose the primary reason for rejection",
    },
    {
      name: "comments",
      label: "Detailed Comments",
      type: "textarea",
      required: true,
      minLength: 10,
      maxLength: 1000,
      placeholder:
        "Please provide specific details about what needs to be corrected. Include any relevant information that will help the field team resolve the issues.",
      helperText:
        "Provide specific, actionable feedback (minimum 10 characters)",
    },
    {
      name: "attachments",
      label: "Attachments (Optional)",
      type: "multi-file",
      multiple: false,
      maxFiles:1,
      accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
      helperText:"You can upload optional supporting files in PDF, Word, or image format (.pdf, .doc, .docx, .jpg, .jpeg, .png)"
    },
  ];

  // Create form configuration
  const formConfig: FormConfig = {
    fields: formFields,
    showSubmitButton: false,
    showResetButton: false,
    layout: "vertical",
    spacing: "medium",
    classes: FIELD_CLASSES,
  };

  // Create form instance
  const form = FormService.createForm(formConfig);

  // Handle form field changes to capture file attachments
  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "attachments") {
      // DynamicForm sends a single file for multi-file fields when multiple: false
      const filesArray = Array.isArray(value) ? value : value ? [value] : [];
      setAttachments(filesArray);
    }
  };

  // Handle form submission - show confirmation dialog first
  const handleSubmit = async (formData: any) => {
    setPendingFormData(formData);
    setShowConfirmationDialog(true);
  };

// Handle final confirmation after user confirms in dialog
const handleFinalConfirm = async () => {
  if (pendingFormData) {
    console.log("Pending Form Data Object:", pendingFormData);
    const formData = new FormData();

    formData.append("remark", pendingFormData.comments);
    formData.append("rejected_by", userName);
    formData.append("rejection_reason", pendingFormData.reason);

    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        formData.append("rejected_proof", file);
      });
    }
    
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
    console.log("---------------------------");


    try {
      await rejectServiceOrder.mutateAsync({
        id: id,
        payload: formData,
      });
            toast({
        title: "Service Order Rejected",
        description: "The service order has been rejected and sent back to the field team.",
        variant: "default",
      });

      
      setShowConfirmationDialog(false);
      setPendingFormData(null);
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
       toast({
        title: "Rejection Failed",
        description: "Failed to reject the service order. Please try again.",
        variant: "destructive",
      });
    }
   
  }
};

  // Handle confirmation dialog close
  const handleConfirmationClose = () => {
    setShowConfirmationDialog(false);
    setPendingFormData(null);
  };

  const handleClose = () => {
    form.reset();
    setAttachments([]);
    setPendingFormData(null);
    setShowConfirmationDialog(false);
    onClose();
  };

  // Custom footer component
  const CustomFooter = () => {
    const hasErrors = FormService.hasErrors(form);
    const formValues = form.getValues();
    const isValid =
      formValues.reason &&
      formValues.comments &&
      formValues.comments.length >= 10;

    return (
      <DialogFooter>
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={form.handleSubmit(handleSubmit)}
          disabled={rejectServiceOrder.isPending}
        >
          {rejectServiceOrder.isPending ? "Rejecting..." : "Confirm Rejection"}
        </Button>
      </DialogFooter>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Reject Service Order
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-red-800">Important Notice</h4>
                  <p className="text-sm text-red-700 mt-1">
                    Rejecting this service order will send it back to the field
                    team for corrections. Please provide specific, actionable
                    feedback to help resolve the issues.
                  </p>
                </div>
              </div>
            </div>

            {/* Dynamic Form */}
            <DynamicForm 
              fields={formFields} 
              form={form} 
              config={formConfig} 
              onFieldChange={handleFieldChange}
            />
          </div>
          <CustomFooter />
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={showConfirmationDialog}
        onClose={handleConfirmationClose}
        onConfirm={handleFinalConfirm}
        title="Confirm Service Order Rejection"
        description="Are you sure you want to reject this service order? This will send it back to the field team for corrections."
        confirmText="Yes, Reject Order"
        cancelText="Cancel"
      />
    </>
  );
};

export default RejectServiceOrderModal;
